import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { View, Keyboard } from 'react-native'
import { actionTypes, dataTypes, sourceTypes } from '@adalo/constants'

// ducks
import { getItemById } from '../ducks/data'
import { changeValue } from '../ducks/formInputs'
import MissingComponent from './MissingComponent'

// utils
import { executeAction, hasLink, actionContextTypes } from '../utils/actions'
import { deepGet, deepSet } from '../utils/objects'
import { getUserValue, setUserValue } from '../utils/user-datastore'
import { solveBindings } from '../utils/library-binding'
import { oauth } from '../utils/oauth'
import { normalizeStyles } from '../utils/styles'
import { normalizeFontFamily } from '../utils/type'

class LibraryComponent extends Component {
  constructor(constructorProps) {
    super(constructorProps)

    this.setupState()
  }

  static contextTypes = {
    ...actionContextTypes,
    getLibraryComponent: PropTypes.func,
  }

  setupState = () => {
    let manifest = this.getManifest()

    if (manifest) {
      let props = manifest.props || []
      let childComponents = manifest.childComponents || []

      let { componentProps } = this.props

      const stateObject = { props: {}, childComponents: {} }

      props.forEach(prop => {
        if (prop.role === 'formValue') {
          stateObject.props = {
            ...stateObject.props,
            [prop.name]: {
              lastInitial: componentProps[prop.name]
                ? componentProps[prop.name].initial
                : undefined,
            },
          }
        }
      })
      childComponents.forEach(child => {
        stateObject.childComponents = {
          ...stateObject.childComponents,
          [child.name]: {},
        }

        child.props.forEach(prop => {
          if (prop.role === 'formValue') {
            stateObject.childComponents[child.name] = {
              ...stateObject.childComponents[child.name],
              [prop.name]: {
                lastInitial:
                  componentProps[child.name] &&
                  componentProps[child.name][prop.name]
                    ? componentProps[child.name][prop.name].initial
                    : undefined,
              },
            }
          }
        })
      })

      this.state = stateObject
    }
  }

  getUserValue = key => {
    let { object } = this.props
    let { libraryName } = object.attributes
    let { getBaseURL, getApp } = this.context
    let baseURL = getBaseURL()

    return getUserValue(getApp(), baseURL, libraryName, key)
  }

  setUserValue = (key, value) => {
    let { object } = this.props
    let { libraryName } = object.attributes
    let { getBaseURL, getApp } = this.context
    let baseURL = getBaseURL()

    return setUserValue(getApp(), baseURL, libraryName, key, value)
  }

  handleAction = (action, key, itm, i) => async (...args) => {
    let value = args[0]
    let { getBindings, getBinding, getStore, setDirty } = this.context
    let bindings = {}

    if (itm) {
      let { datasourceId, tableId, collectionId } = itm._meta
      let key = `${datasourceId}.${tableId || collectionId}`

      bindings[key] = itm.id
    }

    let { object, dispatch, bindingValues } = this.props
    let { actions } = object

    if (key) {
      //this changes the value of the text input
      let inputId = `${object.id}.${key.join('.')}`
      dispatch(changeValue(inputId, value))
      await Promise.resolve()
    }

    if (!action) {
      return
    }

    let { actionId } = action
    bindingValues = bindingValues || {}

    if (!action || !actions || !actions[actionId]) {
      return
    }

    let localGetBindings = () => ({ ...getBindings(), ...bindings })

    let state = getStore().getState()

    let localGetBinding = listId => {
      if (listId in bindingValues) {
        let dep = bindingValues[listId] && bindingValues[listId][i]
        return getItemById(state, dep._meta.tableId, dep.id)
      }

      return getBinding(listId)
    }

    let localGetActionArguments = index => {
      return args[index]
    }
    await executeAction(actions[actionId], {
      ...this.context,
      dispatch,
      getBinding: localGetBinding,
      getBindings: localGetBindings,
      getActionArguments: localGetActionArguments,
    })

    if (hasLink(actions[actionId])) {
      Keyboard.dismiss()
    }

    setDirty()
  }

  handleAutosaveChange = (
    propName,
    childComponent = null,
    opts = {}
  ) => async newValue => {
    let {
      object: { attributes },
      dispatch,
    } = this.props

    let { setDirty } = this.context

    let manifest = this.getManifest()

    let prop
    let propValue

    if (childComponent) {
      const child = manifest.childComponents.find(
        itm => itm.name === childComponent
      )
      prop = child.props.find(itm => itm.name === propName)
      propValue = attributes[childComponent][propName]
    } else {
      prop = manifest.props.find(itm => itm.name === propName)
      propValue = attributes[propName]
    }

    let action

    if (!propValue) {
      return
    }

    // TODO: if it's a collection, do something different
    if (
      prop.type === dataTypes.BOOLEAN &&
      prop.role === 'autosaveInput' &&
      propValue.source &&
      propValue.source.type === sourceTypes.AUTOSAVE
    ) {
      let actionType = newValue
        ? actionTypes.CREATE_ASSOCIATION
        : actionTypes.DELETE_ASSOCIATION

      let { source } = propValue

      if (!source) {
        return
      }

      action = {
        id: 'syntheticAction',
        actionType,
        options: {
          fieldId: source.source.fieldId,
          object1: source.source.source,
          object2: source.value,
          ...opts,
        },
      }
    } else {
      let fieldId = propValue.source && propValue.source.fieldId
      let source = propValue.source && propValue.source.source

      if (!source || !fieldId) {
        console.warn('ERROR: Could not update autosave value')
        return
      }

      action = {
        id: 'syntheticAction',
        actionType: actionTypes.UPDATE_OBJECT,
        options: {
          bindingId: source,
          fields: [{ fieldId, source: newValue }],
          ...opts,
        },
      }
    }

    await executeAction({ actions: [action] }, { ...this.context, dispatch })
    setDirty()
  }

  handleFormValueChange = (
    propName,
    childComponent = null
  ) => async newValue => {
    const { object, dispatch } = this.props
    const inputId = childComponent
      ? `${object.id}.${childComponent}.${propName}`
      : `${object.id}.${propName}`

    await dispatch(changeValue(inputId, newValue))
  }

  getManifest = () => {
    const { app, object } = this.props

    const {
      attributes: { libraryName, componentName },
    } = object

    if (app?.libraryComponentManifests?.[libraryName]?.[componentName]) {
      return app.libraryComponentManifests[libraryName][componentName]
    } else if (object.libraryComponentManifest) {
      return object.libraryComponentManifest
    } else {
      console.error(
        'Manifest not found for component',
        componentName,
        'from library',
        libraryName
      )
      return null
    }
  }

  checkAuth = manifest => {
    if (manifest.auth) {
      const {
        auth: { config, mapping },
      } = manifest
      const { dispatch } = this.props

      const auth = async (
        token,
        clientId,
        hostedDomain = null,
        user = null
      ) => {
        let helpers = { dispatch, ...this.context }
        let oAuthConfig = {
          token,
          clientId,
          hostedDomain,
          ...config,
          mapping,
          user,
        }
        try {
          let retcode = await oauth(oAuthConfig, helpers)
          return retcode
        } catch (error) {
          console.log('oauth error: ', error)
        }
      }
      return auth
    }
    return null
  }

  getAttributes = () => {
    let { object, componentProps, getApp } = this.props
    let { actionRefs, attributes } = object
    let result = componentProps
    let manifest = this.getManifest()
    if (!manifest) return {}
    let props = manifest.props || []
    let childComponents = manifest.childComponents || []
    let propMap = {}

    const { branding } = getApp() || {}

    let auth = this.checkAuth(manifest)
    if (auth) {
      result.oauth = auth
    }

    let autosaves = manifest.props.filter(prop => prop.role === 'autosaveInput')
    let formValues = manifest.props.filter(prop => prop.role === 'formValue')

    props.forEach(prop => {
      propMap[prop.name] = prop

      if (prop.styles) {
        result = deepSet(
          result,
          ['styles', prop.name],
          normalizeStyles(prop, attributes, branding)
        )
      }
    })

    childComponents.forEach(child => {
      propMap[child.name] = child
      child.props.forEach(prop => {
        propMap[child.name][prop.name] = prop

        // custom fonts
        if (prop.styles) {
          result = deepSet(
            result,
            [child.name, 'styles', prop.name],
            normalizeStyles(prop, attributes[child.name], branding)
          )
        }

        // Autosave Inputs for Child Component Props
        if (prop.role === 'autosaveInput') {
          if (child.role === 'listItem') {
            const { reference } = child
            result[reference] =
              Array.isArray(result[reference]) &&
              result[reference].map(itm => {
                const value = itm[child.name][prop.name]
                const { datasourceId, tableId } = itm._meta
                const onChange = this.handleAutosaveChange(
                  prop.name,
                  child.name,
                  { objectId: itm.id, datasourceId, tableId }
                )
                itm[child.name][prop.name] = { value, onChange }
                return itm
              })
          } else {
            const propName = prop.name
            const value = result[child.name][propName]
            const onChange = this.handleAutosaveChange(propName, child.name)
            result[child.name][propName] = { value, onChange }
          }
        }

        //formValues for child components
        if (prop.role === 'formValue' && child.role !== 'listItem') {
          //Doesn't currently work with the list item role
          const propName = prop.name
          const initial = result[child.name][propName]
            ? result[child.name][propName].initial
            : undefined
          let value = result[child.name][propName]
            ? result[child.name][propName].value
            : undefined
          const onChange = this.handleFormValueChange(propName, child.name)

          result[child.name][propName] = { value, onChange, initial }
        }
      })
    })

    actionRefs.forEach(actionRef => {
      let key = actionRef.split('.')
      let setterKey = key.slice(1)

      let action = deepGet(object, key)

      let refKey

      let parentKey = key[1]
      let parent = propMap[parentKey]

      if (parent && parent.role === 'listItem') {
        let listData = result[parent.reference]

        if (!listData) {
          return
        }

        listData = listData.map((itm, i) => {
          return deepSet(
            itm,
            setterKey,
            this.handleAction(action, refKey, itm, i)
          )
        })

        result = { ...result, [parent.reference]: listData }
      } else {
        result = deepSet(result, setterKey, this.handleAction(action, refKey))
      }
    })

    autosaves.forEach(prop => {
      let key = prop.name
      let value = result[key]

      let onChange = this.handleAutosaveChange(key)

      result[key] = { value, onChange }
    })

    formValues.forEach(prop => {
      let key = prop.name
      let onChange = this.handleFormValueChange(key)
      let initial = result[key] ? result[key].initial : undefined
      let value = result[key] ? result[key].value : undefined

      result[key] = { value, onChange, initial }
    })

    return result
  }

  checkFormValues = (mount = false) => {
    let { object, componentProps, dispatch } = this.props

    let manifest = this.getManifest()
    if (!manifest) return
    let props = manifest.props || []
    let childComponents = manifest.childComponents || []

    props.forEach(prop => {
      if (prop.role === 'formValue') {
        const {
          props: {
            [prop.name]: { lastInitial },
          },
        } = this.state

        if (
          (componentProps[prop.name] &&
            componentProps[prop.name].value === undefined &&
            componentProps[prop.name].initial !== undefined) ||
          (mount &&
            componentProps[prop.name] &&
            componentProps[prop.name].initial) ||
          (componentProps[prop.name] &&
            lastInitial !== componentProps[prop.name].initial)
        ) {
          if (componentProps[prop.name].value === lastInitial || mount) {
            dispatch(
              changeValue(
                `${object.id}.${prop.name}`,
                componentProps[prop.name].initial || ''
              )
            )
          } else if (componentProps[prop.name].value === undefined) {
            dispatch(changeValue(`${object.id}.${prop.name}`, ''))
          }

          this.setState(state => {
            return {
              ...state,
              props: {
                ...state.props,
                [prop.name]: {
                  lastInitial: componentProps[prop.name].initial,
                },
              },
            }
          })
        }
      }
    })

    childComponents.forEach(child => {
      child.props.forEach(prop => {
        if (prop.role === 'formValue') {
          const {
            childComponents: {
              [child.name]: {
                [prop.name]: { lastInitial },
              },
            },
          } = this.state

          if (
            (componentProps[child.name] &&
              componentProps[child.name][prop.name] &&
              componentProps[child.name][prop.name].value === undefined &&
              componentProps[child.name][prop.name].initial !== undefined) ||
            (mount &&
              componentProps[child.name] &&
              componentProps[child.name][prop.name] &&
              componentProps[child.name][prop.name].initial) ||
            (componentProps[child.name] &&
              componentProps[child.name][prop.name] &&
              lastInitial !== componentProps[child.name][prop.name].initial)
          ) {
            if (
              componentProps[child.name][prop.name].value === lastInitial ||
              mount
            ) {
              dispatch(
                changeValue(
                  `${object.id}.${child.name}.${prop.name}`,
                  componentProps[child.name][prop.name].initial || ''
                )
              )
            } else if (
              componentProps[child.name][prop.name].value === undefined
            ) {
              dispatch(
                changeValue(`${object.id}.${child.name}.${prop.name}`, '')
              )
            }

            this.setState(state => {
              return {
                ...state,
                childComponents: {
                  ...state.childComponents,
                  [child.name]: {
                    ...state.childComponents[child.name],
                    [prop.name]: {
                      lastInitial:
                        componentProps[child.name][prop.name].initial,
                    },
                  },
                },
              }
            })
          }
        }
      })
    })
  }

  getLayout = () => {
    let { object } = this.props
    let { layout } = object
    let manifest = this.getManifest()
    if (!manifest) return {}

    let height

    if (manifest.resizeY) {
      height = object.attributes.height
    }

    return {
      height,
      ...layout,
    }
  }

  getNormalizedBrandingFonts = () => {
    const { app = {} } = this.props
    const { branding = {} } = app

    return {
      body: normalizeFontFamily('@body', branding),
      heading: normalizeFontFamily('@heading', branding),
    }
  }

  componentDidMount() {
    if (this.getManifest()) {
      this.checkFormValues(true)
    }
  }

  componentDidUpdate() {
    if (this.getManifest()) {
      this.checkFormValues(false)
    }
  }

  render() {
    let { object, active, topScreen } = this.props
    let { libraryName, componentName } = object.attributes
    let { getLibraryComponent, getApp } = this.context

    let ComponentClass = getLibraryComponent(libraryName, componentName)

    if (!ComponentClass || !this.getManifest()) {
      return <MissingComponent name={`${libraryName}.${componentName}`} />
    }

    let augmentedAttributes = this.getAttributes()

    let componentProps = {
      ...augmentedAttributes,
      appId: getApp().id,
      active,
      topScreen: !!topScreen,
      _fonts: this.getNormalizedBrandingFonts(),
      _height: object.attributes.height,
      _width: object.attributes.width,
      _getUserValue: this.getUserValue,
      _setUserValue: this.setUserValue,
    }

    let layout = this.getLayout()
    return (
      <View style={[layout]}>
        <ComponentClass {...componentProps} />
      </View>
    )
  }
}

const mapStateToProps = (state, props) => {
  let {
    object,
    getApp,
    getBinding,
    getBindingsList,
    getParams,
    getAssetURL,
    getFileUploadsBaseURL,
    getImageUploadsBaseURL,
    listItemId,
  } = props

  object.attributes = { ...object.attributes, listItemId }

  const app = getApp && getApp()

  return solveBindings(app, object, {
    state,
    getApp,
    getBinding,
    getBindingsList,
    getParams,
    getAssetURL,
    getFileUploadsBaseURL,
    getImageUploadsBaseURL,
    listItemId,
  })
}

const ConnectedLibraryComponent = connect(mapStateToProps)(LibraryComponent)

export default class WrappedLibraryComponent extends Component {
  static contextTypes = {
    getApp: PropTypes.func,
    getBinding: PropTypes.func,
    getBindingsList: PropTypes.func,
    getParams: PropTypes.func,
    getFileUploadsBaseURL: PropTypes.func,
    getImageUploadsBaseURL: PropTypes.func,
    getAssetURL: PropTypes.func,
  }

  render() {
    return <ConnectedLibraryComponent {...this.props} {...this.context} />
  }
}
